import { defineStore } from 'pinia';
import { nextTick } from 'vue';
import DemoService from '@/services/demo';
import { stepsCount, stepsInOrder, stepSlugs } from '@/components/demo/stepSlugs';
import { trackStepBySlug } from '@/components/demo/demoTracking';
import { getFingerprintVisitorId } from '@/utils/fingerprint';
import { useAuthStore } from '@/stores/auth';

/** A generated visitor question, tagged with the persona it belongs to */
interface DemoQuestion {
  /** Unique question id from the API */
  id: string;
  /** The question text */
  text: string;
  /** Whether the user kept this question selected */
  checked: boolean;
  /** The persona this question was generated for */
  persona: { id: string; name: string; description: string };
}

interface DemoContact {
  /** Company name */
  company: string;
  /** Contact person name */
  name: string;
  /** Contact e-mail address */
  email: string;
  /** Contact phone number */
  phone: string;
  /** Whether the privacy policy was accepted */
  privacyAccepted: boolean;
  /** Whether marketing communication was accepted */
  marketing: boolean;
}

interface DemoError {
  code: number;      // Error code from the API. Can be used to override message with localization
  message: string;  // Error text from the API
  step: string;     // The step in which the error occurred
}

interface DemoState {
  /** Demo user id (from local storage or created via fingerprint) */
  userId: string | null;
  /** Current wizard step slug (one of stepSlugs) */
  step: string;
  /** The company website URL entered by the user */
  companyUrl: string;
  /** Total number of wizard steps */
  stepsAmount: number;
  /** True while a step-blocking request is running */
  loading: boolean;
  /** All generated questions (across personas) */
  questions: DemoQuestion[];
  /** Whether questions need to be (re-)generated */
  isQuestionsDirty: boolean;
  /** Created demo intents */
  intents: any[];
  /** Generated feature suggestions; null while not loaded */
  features: any | null;
  /** Contact form data for demo account creation */
  contact: DemoContact;
  // Array of lowercase service names/identifiers the site uses/we suggest
  technologies: string[];
  persona: {
    // The type of persona selected
    id: string;
    // The description prompt for the selected persona, in the current language
    prompt: string;
  };
  /** Generated introduction/purpose text for the AI agent */
  agentIntroduction: string;
  /** Browser fingerprint visitor id; null until resolved */
  fingerprintId: string | null;
  /** Query params the demo page was opened with */
  queryParams: { key: string; value: string }[];
  /** True when the last API call failed */
  hasError: boolean;
  /** Details of the last error */
  error: DemoError;
  event: string | null; //String if set E.g. 'CCW 2023'
}

/**
 * Pinia conversion of legacy vuex module `src/store/modules/demo.ts` (namespace `demo`).
 * In-memory only — no `vuexPersist` option (legacy module was not in either `vuexLocal`/`vuexSession`
 * reducer in `src/store/index.ts`).
 *
 * Collisions & renames (see
 * `/private/tmp/claude-501/-Users-haslien-Documents-GitHub-moin-hub/23091b27-f9d9-4b85-91e6-ff6cd1c12710/scratchpad/pinia-migration/RENAMES.md`):
 * - Pinia merges state and getters onto a single `this`, so every legacy getter that shared its
 *   name with a state prop is a REAL collision here. Legacy had one such getter per state prop it
 *   exposed (`step`, `hasError`, `stepsAmount`, `loading`, `contact`, `companyUrl`, `questions`,
 *   `isQuestionsDirty`, `intents`, `fingerprintId`, `userId`, `queryParams`, `event`, `features`,
 *   `technologies`, `agentIntroduction`, `persona`, `error`). In every case the STATE PROP keeps
 *   its name and the GETTER is renamed `<name>Getter` (matches the pattern already used in
 *   `src/stores/auth.ts`). Most of these were pure identity passthroughs (`state.X` → same value
 *   available directly on the store as `this.X`); `questionsGetter` is the one exception — it is
 *   NOT an identity passthrough (it filters `state.questions` down to the currently-selected
 *   persona), so internal actions that used to read `getters.questions` now read
 *   `this.questionsGetter` (NOT `this.questions`, which stays the raw unfiltered array).
 * - `test` getter is kept as-is (not renamed): `state.test` was never part of `DemoState` — it's
 *   pre-existing dead/buggy code kept for compatibility, so there is no real collision.
 * - Legacy mutations (`setValue`, `setQueryParams`, `resetAll`, `nextStep`, `previousStep`,
 *   `toggleQuestion`, `setError`) become same-named actions — none of them collided with an
 *   existing action of the same name, so this is a straight mutation→action conversion, not a
 *   merge.
 * - Cross-namespace access, resolved via the sibling store (called INSIDE the action body):
 *   `rootGetters['auth/user']` → `useAuthStore().userGetter` (NOT `.user` — `auth`'s legacy getter
 *   `user` was itself renamed to `userGetter` due to its own state/getter collision, and it is the
 *   getter — not the raw decoded-JWT state prop — that legacy `demo/initializeUserIds` read).
 */
export const useDemoStore = defineStore('demo', {
  state: (): DemoState => ({
    userId: '',
    step: stepSlugs.stepCompany,
    companyUrl: '',
    stepsAmount: stepsCount,
    loading: false,
    questions: [],
    isQuestionsDirty: true,
    intents: [],
    features: null,
    contact: {
      company: '',
      name: '',
      email: '',
      phone: '',
      privacyAccepted: false,
      marketing: false,
    },
    // Array of lowercase service names/identifiers the site uses/we suggest
    technologies: [],
    persona: {
      // The type of persona selected
      id: '',
      // The description prompt for the selected persona, in the current language
      prompt: '',
    },
    agentIntroduction: '',
    fingerprintId: null,
    queryParams: [],
    hasError: false,
    error: {
      code: 0,      // Error code from the API. Can be used to override message with localization
      message: '',  // Error text from the API
      step: '',     // The step in which the error occurred
    },
    event: null, //String if set E.g. 'CCW 2023'
  }),
  getters: {
    /** Renamed from legacy getter `step` (collides with state prop `step`). Identity passthrough. */
    stepGetter(): string {
      return this.step;
    },
    /** Renamed from legacy getter `hasError` (collides with state prop `hasError`). Identity passthrough. */
    hasErrorGetter(): boolean {
      return this.hasError;
    },
    /** Renamed from legacy getter `stepsAmount` (collides with state prop `stepsAmount`). Identity passthrough. */
    stepsAmountGetter(): number {
      return this.stepsAmount;
    },
    /** Renamed from legacy getter `loading` (collides with state prop `loading`). Identity passthrough. */
    loadingGetter(): boolean {
      return this.loading;
    },
    // pre-existing: 'test' is not part of the state — getter kept for compatibility
    test: (state: any) => state.test,
    /** Renamed from legacy getter `contact` (collides with state prop `contact`). Identity passthrough. */
    contactGetter(): DemoContact {
      return this.contact;
    },
    /** Renamed from legacy getter `companyUrl` (collides with state prop `companyUrl`). Identity passthrough. */
    companyUrlGetter(): string {
      return this.companyUrl;
    },
    /**
     * Renamed from legacy getter `questions` (collides with state prop `questions`). NOT an
     * identity passthrough: filters the raw `questions` state down to the currently-selected
     * persona (or returns all of them if no persona is selected yet).
     */
    questionsGetter(): DemoQuestion[] {
      // If no persona selected, return all
      if (!this.persona.id) return this.questions;
      // ... else return all for the selected persona
      return this.questions.filter(q => q.persona.id === this.persona.id);
    },
    /** Renamed from legacy getter `isQuestionsDirty` (collides with state prop `isQuestionsDirty`). Identity passthrough. */
    isQuestionsDirtyGetter(): boolean {
      return this.isQuestionsDirty;
    },
    /** Renamed from legacy getter `intents` (collides with state prop `intents`). Identity passthrough. */
    intentsGetter(): any[] {
      return this.intents;
    },
    /** Renamed from legacy getter `fingerprintId` (collides with state prop `fingerprintId`). Identity passthrough. */
    fingerprintIdGetter(): string | null {
      return this.fingerprintId;
    },
    /** Renamed from legacy getter `userId` (collides with state prop `userId`). Identity passthrough. */
    userIdGetter(): string | null {
      return this.userId;
    },
    /** Renamed from legacy getter `queryParams` (collides with state prop `queryParams`). Identity passthrough. */
    queryParamsGetter(): { key: string; value: string }[] {
      return this.queryParams;
    },
    /** Renamed from legacy getter `event` (collides with state prop `event`). Identity passthrough. */
    eventGetter(): string | null {
      return this.event;
    },
    /** Renamed from legacy getter `features` (collides with state prop `features`). Identity passthrough. */
    featuresGetter(): any | null {
      return this.features;
    },
    /** Renamed from legacy getter `technologies` (collides with state prop `technologies`). Identity passthrough. */
    technologiesGetter(): string[] {
      return this.technologies;
    },
    /** Renamed from legacy getter `agentIntroduction` (collides with state prop `agentIntroduction`). Identity passthrough. */
    agentIntroductionGetter(): string {
      return this.agentIntroduction;
    },
    /** Renamed from legacy getter `persona` (collides with state prop `persona`). Identity passthrough. */
    personaGetter(): { id: string; prompt: string } {
      return this.persona;
    },
    /** Renamed from legacy getter `error` (collides with state prop `error`). Identity passthrough. */
    errorGetter(): DemoError {
      return this.error;
    },
    personaIds: () => [
      'customerSupport',
      'preSalesConsultant',
      'salesRepresentative',
      'technicalSupport',
      'onboardingSpecialist',
      'customerSuccessManager',
    ],
    /**
     * Get the URL of a logo/icon for a service/feature
     * @returns {(identifier: string) => string}
     */
    getLogo: () => (identifier: string) => {
      return `/brand-logos/${identifier}.svg`;
    },
  },
  actions: {
    async initializeUserIds() {
      const localUserId = DemoService.getLocalUserId();
      this.fingerprintId = await getFingerprintVisitorId();

      if (localUserId && localUserId !== 'null') {
        return;
      }

      const authStore = useAuthStore();
      const username = authStore.userGetter?.username;

      const res: any = await DemoService.getUserIdWithFingerprintId({
        fingerprintId: username || this.fingerprintId,
      }, this.queryParams || []);

      const newUserId = res?.userId || null;

      this.userId = newUserId;

      DemoService.setLocalUserId(newUserId);

      return true;
    },
    /**
     * Starts generating typical questions visitors might have for the website.
     * Loads in the background and populates `questions` when ready.
     * @param {'de'|'en'|string} language The current user's language
     * @param {{id: string, name: string, description: string}[]} personas Generate questions for these personas
     * @returns {Promise<null | {id: string, text: string, checked: boolean, persona: {id: string, name: string, description: string}}[]>}
     */
    async loadQuestionsWithCompany({ language, personas }: { language: string; personas: { id: string; name: string; description: string }[] }) {
      const step = this.step; // Copy: it can change while we await.

      const result: any = await DemoService.getQuestionsWithCompanyUsingPersonas({
        site: this.companyUrl,
        language,
        // `fingerprintId` isn't in the service's param type, but legacy passed it here too
        // (the service just never reads it for this call) — cast to preserve the exact payload.
        fingerprintId: this.fingerprintId,
        personas,
        userId: this.userId,
      } as any);

      if (result.status !== 'OK') {
        this.setError({
          code: result.code,
          message: result.message,
          step,
        });

        if (result.message === '404') this.step = stepSlugs.stepCompany;
        this.hasError = true;
        return null;
      }

      // The questions will contain all possible questions, and is tagged with which persona it'd belong to
      this.questions = result.personas.flatMap((persona: any) => {
        return persona.queries.map((query: any) => ({
          id: query.id,
          text: query.text,
          checked: true,
          persona: {
            id: persona.persona.id,
            name: persona.persona.name,
            description: persona.persona.description,
          },
        }));
      });

      return this.questions;
    },
    /**
     * Start analyzing what technologies/services the website uses.
     * Will return with ones we support integrating with that they use, and/or a
     * generic list of suggestions.
     * @param {string} url The company's website
     * @returns {Promise<null | {status: string, technologies: string[]}>}
     */
    async analyzeSiteTechnologies({ url }: { url: string }) {
      const step = this.step; // Copy: it can change while we await.
      this.technologies = [];

      const res: any = await DemoService.analyzeSiteTechnologies({
        url,
        userId: this.userId,
      });

      if (res.status !== 'OK') {
        this.setError({
          code: res.code,
          message: res.message,
          step,
        });

        this.hasError = true;
        return null;
      }

      this.technologies = res.technologies;
      return res;
    },
    /**
     * @deprecated - No longer used
     */
    async handleStepCompany({ language, dirty }: { language: string; dirty: boolean }) {
      const step = this.step; // Copy: it can change while we await.
      // Go to next step directly
      if (!dirty) {
        // TODO Bugfix here. If it failed, this should not happen.
        this.step = stepSlugs.stepQuestions;
        return;
      }

      this.loading = true;
      this.hasError = false;

      this.step = stepSlugs.stepCompanyLoading;

      const result: any = await DemoService.getQuestionsWithCompany({
        site: this.companyUrl,
        language,
        // Legacy passed the possibly-null fingerprintId through untyped (`state: any`); the
        // service param is typed as required `string`, so assert to keep identical behavior.
        fingerprintId: this.fingerprintId!,
        userId: this.userId,
      });

      if (result.status !== 'OK') {
        this.setError({
          code: result.code,
          message: result.message,
          step,
        });
        if (result.message === '404') {
          this.step = stepSlugs.stepCompany;
        }

        this.hasError = true;
        this.loading = false;
        return;
      }

      this.loading = false;
      this.step = stepSlugs.stepQuestions;
    },
    async handleGenerateFeatures({ language }: { language: string }) {
      this.features = null;

      const step = this.step; // Copy: it can change while we await.
      const excludedIds = this.questionsGetter
        .filter((q: DemoQuestion) => !q.checked && q.persona.id === this.persona.id)
        .map((q: DemoQuestion) => q.id);

      const res: any = await DemoService.getFeaturesWithQuestions({
        excludedIds,
        personaIdentifier: this.persona.id,
        userId: this.userId,
        language,
        site: this.companyUrl,
      });

      if (res.status !== 'OK') {
        this.setError({
          code: res.code,
          message: res.message,
          step,
        });
        this.hasError = true;
        return;
      }

      this.features = res.features || null;
    },
    async handleStepContact({ language }: { language: string }) {
      const step = this.step; // Copy: it can change while we await.
      this.loading = true;

      const contact: any = { ...this.contact };
      if (this.event) {
        contact.event = this.event;
      }
      const res: any = await DemoService.createDemoAccount({
        contact,
        userId: this.userId,
        language,
        site: this.companyUrl,
      });
      if (res.status !== 'OK') {
        this.setError({
          code: res.code,
          message: res.message,
          step,
        });
        console.error('DemoService.createDemoAccount failed');
        this.loading = false;
        this.hasError = true;
        return false;
      }

      this.loading = false;
      return true;
    },
    /**
     * Creates and caches the introduction / purpose for the AI Agent
     * @param {string} language
     * @param {string} personaTitle The display name of the persona.
     * Use the `personaId` to get the title from i18n
     * @returns {Promise<void>}
     */
    async handleCreateAgentIntroduction({ language, personaTitle }: { language: string; personaTitle: string }) {
      const step = this.step; // Copy: it can change while we await.
      this.agentIntroduction = '';

      const res: any = await DemoService.createAgentIntroduction({
        language,
        agentTitle: personaTitle,
        agentDescription: this.persona.prompt,
        userId: this.userId,
        site: this.companyUrl,
      });

      if (res.status !== 'OK') {
        this.setError({
          code: res.code,
          message: res.message,
          step,
        });
        console.error('DemoService.createAgentIntroduction failed');
        this.hasError = true;
        return;
      }

      this.agentIntroduction = res.introduction;
    },
    /**
     * Converted from legacy mutation `setValue`. Generic key/value setter — key is dynamic, so
     * index untyped.
     */
    setValue({ key, value }: { key: string; value: any }) {
      (this as any)[key] = value;

      /**
       * If you change persona, it should wipe the custom instruction,
       * because that instruction is bound to the persona.
       */
      if (key === 'persona') this.agentIntroduction = '';
    },
    /** Converted from legacy mutation `setQueryParams`. */
    setQueryParams(params: { key: string; value: string }[]) {
      this.queryParams = params;
    },
    /** Converted from legacy mutation `resetAll`. */
    resetAll() {
      this.isQuestionsDirty = true;
      this.questions = [];
      this.intents = [];
      this.features = null;
      this.contact = {
        company: '',
        name: '',
        email: '',
        phone: '',
        privacyAccepted: false,
        marketing: false,
      };
      this.technologies = [];
      this.agentIntroduction = '';
      this.persona = {
        id: '',
        prompt: '',
      };
    },
    /** Converted from legacy mutation `nextStep`. */
    nextStep() {
      // NextTick: Allow async methods to set the current step in a variable before we change it
      nextTick(() => {
        const currentStepIndex = stepsInOrder.indexOf(this.step);
        trackStepBySlug(this.step, 'next');
        this.step = stepsInOrder[currentStepIndex + 1];
      });
    },
    /** Converted from legacy mutation `previousStep`. */
    previousStep() {
      trackStepBySlug(this.step, 'back');

      const currentStepIndex = stepsInOrder.indexOf(this.step);

      /**
       * Special handling:
       * If it is a loading screen, we'd skip right back to where we
       * came from. So, if the slug includes "loading", skip one more backwards.
       */
      let stepCount = 1;
      if (stepsInOrder[currentStepIndex - 1].toLowerCase().includes('loading')) {
        stepCount = 2;
      }

      this.step = stepsInOrder[currentStepIndex - stepCount];
    },
    /** Converted from legacy mutation `toggleQuestion`. */
    toggleQuestion(question: DemoQuestion) {
      const index = this.questions.indexOf(question);
      if (index === -1) return;

      // If disabling, you cannot turn off the last one
      const toState = !this.questions[index].checked;
      if (toState === false) {
        const qLeft = this.questions.filter(q => q.checked && this.persona.id === q.persona.id).length;
        if (qLeft === 1) return;
      }

      this.questions[index].checked = toState;
    },
    /** Converted from legacy mutation `setError`. */
    setError(payload: { code: number; message: string; step?: string }) {
      let { code, message, step } = payload;
      if (!step) step = this.step;

      /**
       * Race condition:
       * You do step A, which kicks off background loading.
       * Before it finishes, you reach step D, which also starts background loading.
       * Step A fails, setting the error step to A.
       * Step D fails immediately after, setting the error step to D.
       * When you use the retry on the error page, it now assumes it is step D it has to go back to!
       *
       * Solution:
       * If an error step is already set, only overwrite the error payload
       * if the step that comes in is at a lower index than the current.
       */
      if (this.error?.step) {
        const oldIndex = stepsInOrder.indexOf(this.error.step);
        const newIndex = stepsInOrder.indexOf(step);
        if (newIndex > oldIndex) return;
      }

      this.error = { code, message, step };
    },
  },
});
